// One marketing email. Drafts open in the builder, in three steps: Content (the visual email
// builder — drag-and-drop blocks, inline text editing, images, styles), Audience (who gets it and
// who it's from) and Review & send (pre-send checks, the exact email as a chosen client will receive
// it, test send, send now / schedule). Sent emails open their report (delivered, opened, clicked,
// bounced, unsubscribed, per recipient).
import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
  fetchCampaign,
  updateCampaign,
  fetchAudience,
  fetchPreview,
  draftCampaignCopy,
  sendTestCampaign,
  sendCampaign,
  unscheduleCampaign,
  fetchCampaignReport,
  duplicateCampaign,
  pickAbWinner,
} from '../api/marketing.js';
import { fetchLookbookContacts } from '../api/lookbooks.js';
import { fetchCompanies } from '../api/companies.js';
import { storeShort } from '../lib/stores.js';
import { useAuthStore } from '../stores/authStore.js';
import { TYPE_LABEL } from './Marketing.jsx';
import EmailBuilder from '../components/marketing/EmailBuilder.jsx';
import { BLOCKS } from '../components/marketing/blocks.jsx';
import { MERGE_FIELDS, blockRich, richToText } from '../lib/emailRender.js';

const errMsg = (e) => e.response?.data?.error?.message || e.message || 'Something went wrong.';
const field = 'mt-1 w-full rounded-md border border-line bg-surface px-3 py-2 text-sm outline-none focus:border-accent focus:ring-2 focus:ring-accent/30';
const chip = (on) => `rounded-full px-2.5 py-1 text-xs ${on ? 'bg-accent text-white' : 'bg-surface-raised text-text-secondary hover:text-text-primary'}`;

// --- pre-send checks ------------------------------------------------------------------------------------
const KNOWN = new Set(MERGE_FIELDS);
const unknownFields = (s) => [...String(s || '').matchAll(/\{(\w+)\}/g)].map((m) => m[1]).filter((f) => !KNOWN.has(f));
const isLink = (u) => !u || /^(https?:\/\/|mailto:|tel:|\{\w+\})/i.test(String(u).trim());

/** Everything worth fixing before it goes out. level: error (blocks sending) | warn | ok. */
export function checkCampaign(c, summary, { testSent } = {}) {
  const out = [];
  const add = (level, msg, blockId) => out.push({ level, msg, blockId });
  const label = (b) => BLOCKS[b.type]?.label || b.type;
  if (!c.subject.trim()) add('error', 'Add a subject line.');
  else if (c.subject.length > 70) add('warn', `The subject is ${c.subject.length} characters — phones cut it off around 40–60.`);
  if (!String(c.preheader || '').trim()) add('warn', 'Add preview text — inboxes show it right after the subject.');
  if (c.abTest?.enabled) {
    if (!String(c.subjectB || '').trim()) add('error', 'Add subject line B for the A/B test (or turn the test off).');
    else if (c.subjectB.trim() === c.subject.trim()) add('error', 'Subject lines A and B are the same.');
    for (const f of unknownFields(c.subjectB)) add('error', `“{${f}}” in subject B isn’t a personalisation field.`);
    if (!c.trackingConnected) add('warn', 'Open tracking isn’t connected yet (Resend webhook), so the A/B test can’t measure opens — subject A will be used for everyone else.');
    if (summary && (c.abTest.pct === 50 ? Math.ceil(summary.count / 2) : Math.floor((summary.count * c.abTest.pct) / 100)) < 5) add('warn', `The audience is too small for a ${c.abTest.pct}% test slice — everyone will get subject A.`);
  }
  for (const f of [...unknownFields(c.subject), ...unknownFields(c.preheader)]) add('error', `“{${f}}” in the subject or preview text isn’t a personalisation field.`);
  if (!c.blocks.length) add('error', 'The email is empty.');
  const currencies = new Set((summary?.recipients || []).map((r) => r.currency).filter(Boolean));
  for (const b of c.blocks) {
    const id = b.id;
    const texts = [b.type === 'heading' || b.type === 'text' || b.type === 'quote' || b.type === 'split' ? blockRich(b) : '', b.label, b.title, b.author, b.button?.label, ...(b.cols || []).flatMap((x) => [x.html, x.title, x.label])];
    for (const f of new Set(texts.flatMap(unknownFields))) add('error', `“{${f}}” isn’t a personalisation field (${label(b)} block).`, id);
    switch (b.type) {
      case 'heading':
      case 'text':
      case 'quote':
        if (!richToText(blockRich(b))) add('warn', `An empty ${label(b).toLowerCase()} block.`, id);
        break;
      case 'image':
        if (!b.url) add('error', 'An image block has no image.', id);
        else if (!String(b.alt || '').trim()) add('warn', 'Describe the image (alt text) — shown when images are blocked and read aloud to screen-reader users.', id);
        if (!isLink(b.link)) add('error', 'An image links to an invalid address — use https://…', id);
        break;
      case 'split':
        if (!b.image?.url) add('warn', 'An image + text block has no image.', id);
        else if (!String(b.image?.alt || '').trim()) add('warn', 'Describe the image in the image + text block.', id);
        if (!isLink(b.button?.url)) add('error', 'A button links to an invalid address — use https://…', id);
        break;
      case 'columns':
        if ((b.cols || []).some((x) => !isLink(x.url))) add('error', 'A column button links to an invalid address.', id);
        break;
      case 'button':
        if (!isLink(b.url)) add('error', `The “${b.label}” button links to an invalid address — use https://…`, id);
        else if (!b.url) add('ok', `The “${b.label}” button opens each client’s ordering catalogue.`, id);
        break;
      case 'products':
        if (!(b.items || []).length) add('error', 'The products block has no products.', id);
        else if (b.showPrice) add('ok', 'Products show each client’s own price — anything not in a client’s range is left out of their email.', id);
        break;
      case 'bestsellers':
        add('ok', 'Best sellers are worked out per client from their own orders.', id);
        break;
      case 'countdown': {
        const ends = b.source === 'promotion' ? null : b.endsAt;
        if (b.source !== 'promotion' && !ends) add('error', 'Pick when the countdown ends.', id);
        else if (ends && new Date(ends) < new Date()) add('error', 'The countdown has already ended.', id);
        break;
      }
      case 'lookbook':
        if (!b.selection?.collectionId && !(b.selection?.productIds || []).length) add('error', 'Pick a collection for the lookbook.', id);
        break;
      case 'promotion':
        if (!c.discountId) add('warn', 'The promotion block won’t show — this email isn’t linked to a promotion.', id);
        break;
      case 'video':
        if (!b.url || !b.thumbnail) add('error', 'The video block needs a link and a thumbnail.', id);
        break;
      case 'social':
        if ((b.links || []).some((l) => !/^https?:\/\//i.test(l.url || ''))) add('warn', 'A social link has no address — it’ll be left out.', id);
        break;
      default:
    }
    const only = b.only?.currencies || [];
    if (only.length && currencies.size && !only.some((x) => currencies.has(x))) add('warn', `Nobody in the audience buys in ${only.join(' / ')}, so no one will see this ${label(b).toLowerCase()} block.`, id);
  }
  if (summary && summary.count === 0) add('error', 'No one is in the audience yet.');
  if (!testSent) add('warn', 'Send yourself a test and check it on your phone.');
  add('ok', 'Unsubscribe link, sender details and plain-text version are added automatically.');
  return out;
}

// --- audience ------------------------------------------------------------------------------------------
function AudiencePanel({ c, audience, setAudience, summary }) {
  const [companies, setCompanies] = useState([]);
  const [showList, setShowList] = useState(false);
  const [addEmail, setAddEmail] = useState('');
  const [q, setQ] = useState('');
  useEffect(() => {
    fetchCompanies(c.store).then((l) => setCompanies(l.filter((x) => x.isActive))).catch(() => setCompanies([]));
  }, [c.store]);
  const reps = useMemo(() => {
    const m = new Map();
    for (const co of companies) for (const a of co.repAssignments || []) if (a.repUser) m.set(a.repUser.id, a.repUser);
    return [...m.values()];
  }, [companies]);
  const currencies = useMemo(() => [...new Set(companies.flatMap((co) => (co.locations || []).map((l) => l.currencyCode)).filter(Boolean))].sort(), [companies]);
  const set = (patch) => setAudience({ ...audience, ...patch });
  const toggle = (k, v) => set({ [k]: (audience[k] || []).includes(v) ? audience[k].filter((x) => x !== v) : [...(audience[k] || []), v] });
  const src = audience.source || (c.discountId ? 'promotion' : 'all');

  return (
    <div className="rounded-lg border border-line bg-surface p-4 shadow-sm">
      <div className="flex items-baseline justify-between">
        <h3 className="text-sm font-semibold text-text-primary">Who gets it</h3>
        {summary && (
          <span className="text-sm text-text-secondary">
            <strong className="text-text-primary">{summary.count}</strong> {summary.count === 1 ? 'person' : 'people'} at {summary.companies} customer{summary.companies === 1 ? '' : 's'}
          </span>
        )}
      </div>
      <div className="mt-2 flex flex-wrap gap-1.5">
        {c.discountId && <button type="button" onClick={() => set({ source: 'promotion' })} className={chip(src === 'promotion')}>Everyone this promotion targets</button>}
        <button type="button" onClick={() => set({ source: 'all' })} className={chip(src === 'all')}>All {storeShort(c.store)} customers</button>
        <button type="button" onClick={() => set({ source: 'companies' })} className={chip(src === 'companies')}>Pick customers</button>
      </div>
      {c.discountId && (
        <label className="mt-2 flex items-center gap-2 text-sm text-text-secondary">
          <input type="checkbox" checked={Boolean(audience.notOrderedUnderDiscount)} onChange={(e) => set({ notOrderedUnderDiscount: e.target.checked })} className="accent-accent" />
          Only customers who haven’t ordered under “{c.discountName}” yet <span className="text-xs text-text-muted">(checked again at send time)</span>
        </label>
      )}
      {src === 'companies' && (
        <div className="mt-2 rounded-md border border-line">
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search customers…" className="w-full border-b border-line px-2.5 py-1.5 text-sm outline-none" />
          <div className="max-h-44 overflow-y-auto p-1">
            {companies
              .filter((co) => co.name.toLowerCase().includes(q.toLowerCase()))
              .map((co) => (
                <label key={co.id} className="flex items-center gap-2 rounded px-2 py-1 text-sm hover:bg-surface-raised">
                  <input type="checkbox" checked={(audience.companyIds || []).includes(co.id)} onChange={() => toggle('companyIds', co.id)} className="accent-accent" />
                  {co.name}
                </label>
              ))}
          </div>
        </div>
      )}
      <div className="mt-3 flex flex-wrap items-center gap-1.5 text-xs text-text-muted">
        Ordering:
        {[
          ['any', 'Anyone'],
          ['recent', 'Ordered recently'],
          ['lapsed', 'Hasn’t ordered lately'],
          ['never', 'Never ordered'],
        ].map(([k, l]) => (
          <button key={k} type="button" onClick={() => set({ activity: k })} className={chip((audience.activity || 'any') === k)}>
            {l}
          </button>
        ))}
        {['recent', 'lapsed'].includes(audience.activity) && (
          <select value={audience.activityDays || 60} onChange={(e) => set({ activityDays: Number(e.target.value) })} className="rounded-md border border-line bg-surface px-1.5 py-0.5 text-xs text-text-secondary">
            {[30, 60, 90, 120, 180, 365].map((d) => (
              <option key={d} value={d}>
                {audience.activity === 'recent' ? 'in the last' : 'for'} {d} days
              </option>
            ))}
          </select>
        )}
      </div>
      <details className="mt-3">
        <summary className="cursor-pointer text-xs font-medium text-text-muted">Narrow it down</summary>
        <div className="mt-2 space-y-2">
          {reps.length > 0 && (
            <div className="flex flex-wrap items-center gap-1.5 text-xs text-text-muted">
              Rep:
              {reps.map((r) => (
                <button key={r.id} type="button" onClick={() => toggle('repUserIds', r.id)} className={chip((audience.repUserIds || []).includes(r.id))}>{r.name || r.email}</button>
              ))}
            </div>
          )}
          {currencies.length > 1 && (
            <div className="flex flex-wrap items-center gap-1.5 text-xs text-text-muted">
              Buys in:
              {currencies.map((cur) => (
                <button key={cur} type="button" onClick={() => toggle('currencies', cur)} className={chip((audience.currencies || []).includes(cur))}>{cur}</button>
              ))}
            </div>
          )}
          <label className="flex items-center gap-2 text-sm text-text-secondary">
            <input type="checkbox" checked={Boolean(audience.primaryOnly)} onChange={(e) => set({ primaryOnly: e.target.checked })} className="accent-accent" />
            Main contact only (one email per customer)
          </label>
        </div>
      </details>
      {summary && (
        <div className="mt-3 text-xs">
          <button type="button" onClick={() => setShowList(!showList)} className="font-medium text-accent hover:underline">{showList ? 'Hide recipients' : 'See recipients'}</button>
          {summary.suppressed > 0 && <span className="ml-2 text-text-muted">· {summary.suppressed} unsubscribed / bounced — won’t be emailed</span>}
          {showList && (
            <div className="mt-2 max-h-56 overflow-y-auto rounded-md border border-line">
              {summary.recipients.map((r) => (
                <div key={r.email} className="flex items-center justify-between gap-2 border-b border-line px-2 py-1 last:border-0">
                  <span className="min-w-0 truncate">
                    <span className="text-text-primary">{r.company}</span> <span className="text-text-muted">· {r.name || r.email}</span>
                  </span>
                  <button type="button" onClick={() => set({ removeEmails: [...(audience.removeEmails || []), r.email] })} className="shrink-0 text-text-muted hover:text-error">remove</button>
                </div>
              ))}
              {(audience.removeEmails || []).length > 0 && (
                <div className="flex justify-between px-2 py-1 text-text-muted">
                  {audience.removeEmails.length} removed
                  <button type="button" onClick={() => set({ removeEmails: [] })} className="text-accent hover:underline">Undo</button>
                </div>
              )}
            </div>
          )}
          <div className="mt-2 flex gap-2">
            <input value={addEmail} onChange={(e) => setAddEmail(e.target.value)} placeholder="Add someone by email" className="min-w-0 flex-1 rounded-md border border-line px-2 py-1 text-sm" />
            <button
              type="button"
              disabled={!/^\S+@\S+\.\S+$/.test(addEmail)}
              onClick={() => {
                set({ addEmails: [...new Set([...(audience.addEmails || []), addEmail.trim().toLowerCase()])] });
                setAddEmail('');
              }}
              className="rounded-md border border-line px-2 text-sm text-text-secondary disabled:opacity-40"
            >
              Add
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// --- composer ----------------------------------------------------------------------------------------
const STEPS = [
  ['content', 'Content'],
  ['audience', 'Audience'],
  ['review', 'Review & send'],
];

function Composer({ initial, onSent }) {
  const user = useAuthStore((s) => s.user);
  const [c, setC] = useState(initial);
  const [step, setStep] = useState('content');
  const [saving, setSaving] = useState('saved'); // saved | dirty | saving | error
  const [preview, setPreview] = useState(null);
  const [previewAs, setPreviewAs] = useState('');
  const [mobile, setMobile] = useState(false);
  const [summary, setSummary] = useState(null);
  const [senders, setSenders] = useState([]);
  const [msg, setMsg] = useState('');
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState('');
  const [confirm, setConfirm] = useState(null); // null | { when: 'now' | 'later', at }
  const [testSent, setTestSent] = useState(false);
  const [testStaff, setTestStaff] = useState([]); // colleagues (staff user ids) who also get the test
  const timer = useRef(null);
  const version = useRef(0);
  const pendingRef = useRef({}); // edits not yet saved (merged into the next autosave)

  useEffect(() => {
    fetchLookbookContacts().then((l) => setSenders(l.filter((u) => u.role === 'INTERNAL_STAFF'))).catch(() => setSenders([]));
  }, []);

  // Autosave 700ms after the last change, then refresh the preview (+ audience when it changed).
  const refresh = (as = previewAs, audience = true) => {
    fetchPreview(c.id, as).then(setPreview).catch(() => {});
    if (audience) fetchAudience(c.id).then(setSummary).catch(() => {});
  };
  useEffect(() => refresh(), []); // eslint-disable-line react-hooks/exhaustive-deps
  // Saves run one at a time (chained), so an older body can never land after a newer one.
  const chain = useRef(Promise.resolve());
  const save = () => {
    chain.current = chain.current.then(async () => {
      const body = pendingRef.current;
      if (!Object.keys(body).length) return;
      pendingRef.current = {};
      const v = version.current;
      setSaving('saving');
      try {
        const saved = await updateCampaign(c.id, body);
        setC((x) => ({ ...x, status: saved.status }));
        if (v === version.current && !Object.keys(pendingRef.current).length) setSaving('saved');
        setErr('');
        refresh(previewAs, 'audience' in body);
      } catch (e) {
        pendingRef.current = { ...body, ...pendingRef.current }; // keep it for the next try
        setSaving('error');
        setErr(errMsg(e));
      }
    });
    return chain.current;
  };
  const edit = (patch) => {
    setC((x) => ({ ...x, ...patch }));
    setSaving('dirty');
    version.current++;
    pendingRef.current = { ...pendingRef.current, ...patch };
    clearTimeout(timer.current);
    timer.current = setTimeout(save, 700);
  };
  const flush = async () => {
    clearTimeout(timer.current);
    await save();
    if (Object.keys(pendingRef.current).length) throw new Error('Your latest changes couldn’t be saved — try again.');
  };
  // Don't lose the last keystrokes when leaving the page.
  useEffect(() => {
    const h = (e) => {
      if (Object.keys(pendingRef.current).length) {
        e.preventDefault();
        e.returnValue = '';
      }
    };
    window.addEventListener('beforeunload', h);
    return () => {
      window.removeEventListener('beforeunload', h);
      clearTimeout(timer.current);
      save();
    };
  }, [c.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const run = async (key, fn) => {
    setBusy(key);
    setErr('');
    setMsg('');
    try {
      await flush();
      await fn();
    } catch (e) {
      setErr(errMsg(e));
    } finally {
      setBusy('');
    }
  };
  const aiDraft = async (brief) => {
    await flush();
    return draftCampaignCopy(c.id, brief);
  };
  const test = () =>
    run('test', async () => {
      const extra = senders.filter((u) => testStaff.includes(u.id)).map((u) => u.email);
      const r = await sendTestCampaign(c.id, previewAs || undefined, extra);
      setMsg(r.variants > 1 ? `Two tests (subject A and B) sent to ${r.sentTo}.` : `Test sent to ${r.sentTo}.`);
      setTestSent(true);
    });
  const send = (at) =>
    run('send', async () => {
      const r = await sendCampaign(c.id, at ? new Date(at).toISOString() : null);
      setConfirm(null);
      onSent(r);
    });
  const goto = (s) => {
    flush().catch(() => {});
    if (s === 'review') refresh();
    setStep(s);
  };

  const checks = useMemo(() => checkCampaign(c, summary, { testSent }), [c, summary, testSent]);
  const errors = checks.filter((x) => x.level === 'error');
  const [focus, setFocus] = useState(null);
  const nothing = !summary || summary.count === 0;

  return (
    <div className="space-y-3">
      {/* step bar */}
      <div className="flex flex-wrap items-center gap-2">
        <nav className="flex items-center gap-1 rounded-lg border border-line bg-surface p-1 shadow-sm">
          {STEPS.map(([k, l], i) => (
            <button key={k} type="button" onClick={() => goto(k)} className={`flex items-center gap-2 rounded-md px-3 py-1.5 text-sm font-medium ${step === k ? 'bg-accent text-white' : 'text-text-secondary hover:bg-surface-raised'}`}>
              <span className={`flex h-5 w-5 items-center justify-center rounded-full text-[11px] ${step === k ? 'bg-white/20' : 'bg-surface-raised'}`}>{i + 1}</span>
              {l}
              {k === 'audience' && summary && <span className={`text-xs ${step === k ? 'text-white/80' : 'text-text-muted'}`}>({summary.count})</span>}
              {k === 'review' && errors.length > 0 && <span className="rounded-full bg-error px-1.5 text-[10px] text-white">{errors.length}</span>}
            </button>
          ))}
        </nav>
        <span className="text-xs text-text-muted">
          {TYPE_LABEL[c.type]} · {storeShort(c.store)}
          {c.discountName ? ` · ${c.discountName}` : ''}
        </span>
        <span className={`ml-auto text-xs ${saving === 'error' ? 'text-error' : 'text-text-muted'}`}>{{ saved: '✓ All changes saved', dirty: 'Unsaved…', saving: 'Saving…', error: 'Not saved' }[saving]}</span>
        {step !== 'review' && (
          <button type="button" onClick={() => goto(step === 'content' ? 'audience' : 'review')} className="rounded-md bg-accent px-3 py-1.5 text-sm font-medium text-white hover:bg-accent-hover">
            Next: {step === 'content' ? 'Audience' : 'Review & send'} →
          </button>
        )}
      </div>
      {err && <p className="rounded-md bg-error-light px-3 py-2 text-sm text-error">{err}</p>}

      {step === 'content' && (
        <>
          <div className="grid gap-3 rounded-lg border border-line bg-surface p-3 shadow-sm md:grid-cols-[1fr_1fr_auto]">
            <label className="block text-xs font-medium text-text-muted">
              Subject line <span className={`font-normal ${c.subject.length > 60 ? 'text-warning' : ''}`}>· {c.subject.length}</span>
              <input value={c.subject} onChange={(e) => edit({ subject: e.target.value })} placeholder="e.g. {company}, your Black Friday pricing is live" className={field} />
            </label>
            <label className="block text-xs font-medium text-text-muted">
              Preview text <span className="font-normal">— shown after the subject in the inbox</span>
              <input value={c.preheader} onChange={(e) => edit({ preheader: e.target.value })} className={field} />
            </label>
            <label className="block text-xs font-medium text-text-muted">
              Internal name
              <input value={c.name} onChange={(e) => edit({ name: e.target.value })} className={`${field} md:w-48`} />
            </label>
            <div className="md:col-span-3">
              <label className="flex items-center gap-2 text-xs font-medium text-text-secondary">
                <input type="checkbox" checked={Boolean(c.abTest?.enabled)} onChange={(e) => edit({ abTest: { ...c.abTest, enabled: e.target.checked } })} className="accent-accent" />
                A/B test the subject line
              </label>
              {c.abTest?.enabled && (
                <div className="mt-2 grid gap-3 rounded-md bg-surface-raised/60 p-3 md:grid-cols-[1fr_auto]">
                  <label className="block text-xs font-medium text-text-muted">
                    Subject line B
                    <input value={c.subjectB || ''} onChange={(e) => edit({ subjectB: e.target.value })} placeholder="Try a different angle, e.g. a question or the offer up front" className={field} />
                  </label>
                  <div className="flex flex-wrap items-end gap-3 text-xs text-text-muted">
                    <label className="block">
                      Each version goes to
                      <select value={c.abTest.pct} onChange={(e) => edit({ abTest: { ...c.abTest, pct: Number(e.target.value) } })} className={`${field} w-28`}>
                        {[10, 15, 20, 25, 30, 50].map((v) => (
                          <option key={v} value={v}>{v === 50 ? '50% (no holdout)' : `${v}%`}</option>
                        ))}
                      </select>
                    </label>
                    <label className="block">
                      Pick the winner after
                      <select value={c.abTest.hours} onChange={(e) => edit({ abTest: { ...c.abTest, hours: Number(e.target.value) } })} className={`${field} w-28`}>
                        {[1, 2, 4, 8, 12, 24, 48].map((v) => (
                          <option key={v} value={v}>{v} hour{v === 1 ? '' : 's'}</option>
                        ))}
                      </select>
                    </label>
                  </div>
                  <p className="text-[11px] text-text-muted md:col-span-2">
                    {c.abTest.pct === 50
                      ? 'Half get A, half get B — no one waits; the report shows which did better.'
                      : `${c.abTest.pct}% get A and ${c.abTest.pct}% get B. After ${c.abTest.hours}h the one with more opens (then clicks) goes to the other ${100 - 2 * c.abTest.pct}%. You can also pick the winner yourself from the report.`}
                  </p>
                </div>
              )}
            </div>
          </div>
          <EmailBuilder
            blocks={c.blocks}
            style={c.style}
            subject={c.subject}
            preheader={c.preheader}
            onChange={edit}
            store={c.store}
            discountId={c.discountId}
            discountName={c.discountName}
            previewCtx={preview?.ctx}
            aiDraft={aiDraft}
            focus={focus}
          />
        </>
      )}

      {step === 'audience' && (
        <div className="grid gap-4 lg:grid-cols-[minmax(0,1.4fr)_minmax(0,1fr)]">
          <AudiencePanel c={c} audience={c.audience} setAudience={(a) => edit({ audience: a })} summary={summary} />
          <div className="h-fit space-y-3 rounded-lg border border-line bg-surface p-4 shadow-sm">
            <h3 className="text-sm font-semibold text-text-primary">Who it’s from</h3>
            <label className="block text-xs font-medium text-text-muted">
              Sender
              <select value={c.senderUserId || ''} onChange={(e) => edit({ senderUserId: e.target.value || null })} className={field}>
                <option value="">Each client’s account contact (default CS)</option>
                {senders.map((u) => (
                  <option key={u.id} value={u.id}>
                    {u.name || u.email}
                  </option>
                ))}
              </select>
            </label>
            <p className="text-xs text-text-muted">Replies go to the sender. A personal sender gets noticeably more opens and replies than a brand address.</p>
            <label className="flex items-center gap-2 text-sm text-text-secondary">
              <input type="checkbox" checked={c.copyReps} onChange={(e) => edit({ copyReps: e.target.checked })} className="accent-accent" />
              Let reps know which of their clients got it
            </label>
          </div>
        </div>
      )}

      {step === 'review' && (
        <div className="grid gap-4 xl:grid-cols-[minmax(0,0.9fr)_minmax(0,1.1fr)]">
          <div className="space-y-3">
            <div className="rounded-lg border border-line bg-surface p-4 shadow-sm">
              <h3 className="text-sm font-semibold text-text-primary">Before you send</h3>
              <ul className="mt-2 space-y-1.5">
                {checks.map((x, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm">
                    <span className={`mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center rounded-full text-[10px] font-bold text-white ${x.level === 'error' ? 'bg-error' : x.level === 'warn' ? 'bg-warning' : 'bg-success'}`}>{x.level === 'error' ? '!' : x.level === 'warn' ? '·' : '✓'}</span>
                    <span className="text-text-secondary">
                      {x.msg}
                      {x.blockId && x.level !== 'ok' && (
                        <button
                          type="button"
                          onClick={() => {
                            setFocus({ id: x.blockId, n: Date.now() });
                            setStep('content');
                          }}
                          className="ml-1.5 text-xs font-medium text-accent hover:underline"
                        >
                          Fix
                        </button>
                      )}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="space-y-1 rounded-lg border border-line bg-surface p-4 text-sm shadow-sm">
              <div><span className="text-text-muted">To</span> <strong className="text-text-primary">{summary ? `${summary.count} ${summary.count === 1 ? 'person' : 'people'} at ${summary.companies} customer${summary.companies === 1 ? '' : 's'}` : '…'}</strong> <button type="button" onClick={() => goto('audience')} className="ml-1 text-xs text-accent hover:underline">Change</button></div>
              {preview && (
                <>
                  <div><span className="text-text-muted">From</span> {preview.from} <span className="text-text-muted">· replies to</span> {preview.replyTo}</div>
                  <div><span className="text-text-muted">Subject{c.abTest?.enabled ? ' A' : ''}</span> <strong className="text-text-primary">{preview.subject}</strong></div>
                  {c.abTest?.enabled && c.subjectB && <div><span className="text-text-muted">Subject B</span> <strong className="text-text-primary">{c.subjectB}</strong> <span className="text-xs text-text-muted">(personalised per client)</span></div>}
                  {preview.preheader && <div className="text-text-muted">{preview.preheader}</div>}
                </>
              )}
            </div>
            {msg && <p className="text-sm text-success">{msg}</p>}
            <div className="rounded-lg border border-line bg-surface p-4 shadow-sm">
              <h3 className="text-sm font-semibold text-text-primary">Send a test</h3>
              <div className="mt-2 flex flex-wrap items-center gap-1.5 text-sm">
                <span className="rounded-full bg-surface-raised px-2.5 py-1 text-xs text-text-secondary">You · {user?.email}</span>
                {senders
                  .filter((u) => testStaff.includes(u.id))
                  .map((u) => (
                    <span key={u.id} className="flex items-center gap-1 rounded-full bg-accent-light px-2.5 py-1 text-xs text-text-primary">
                      {u.name || u.email}
                      <button type="button" onClick={() => setTestStaff((l) => l.filter((x) => x !== u.id))} className="text-text-muted hover:text-error" aria-label={`Remove ${u.name || u.email}`}>
                        ✕
                      </button>
                    </span>
                  ))}
                {testStaff.length < 5 && (
                  <select
                    value=""
                    onChange={(e) => e.target.value && setTestStaff((l) => [...l, e.target.value])}
                    className="rounded-md border border-line bg-surface px-2 py-1 text-xs text-text-secondary"
                  >
                    <option value="">+ Add a colleague…</option>
                    {senders
                      .filter((u) => u.email.toLowerCase() !== String(user?.email || '').toLowerCase() && !testStaff.includes(u.id))
                      .map((u) => (
                        <option key={u.id} value={u.id}>
                          {u.name || u.email}
                        </option>
                      ))}
                  </select>
                )}
              </div>
              <button onClick={test} disabled={busy === 'test'} className="mt-3 rounded-md border border-line px-3 py-2 text-sm font-medium text-text-secondary hover:bg-surface-raised disabled:opacity-50">
                {busy === 'test' ? 'Sending…' : testStaff.length ? `Send test to you + ${testStaff.length} colleague${testStaff.length === 1 ? '' : 's'}` : 'Send test to yourself'}
              </button>
              <p className="mt-1.5 text-[11px] text-text-muted">Tests go to TEAMM8 staff only, personalised as the client chosen in “Preview as”.</p>
            </div>
            <div className="flex flex-wrap gap-2">
              <button onClick={() => setConfirm({ when: 'later', at: '' })} disabled={nothing || errors.length > 0} className="rounded-md border border-line px-3 py-2 text-sm font-medium text-text-secondary hover:bg-surface-raised disabled:opacity-50">
                Schedule…
              </button>
              <button onClick={() => setConfirm({ when: 'now' })} disabled={nothing || errors.length > 0} className="rounded-md bg-accent px-4 py-2 text-sm font-semibold text-white hover:bg-accent-hover disabled:opacity-50">
                Send…
              </button>
            </div>
            {errors.length > 0 && <p className="text-xs text-text-muted">Fix the {errors.length === 1 ? 'item' : `${errors.length} items`} marked ! to send.</p>}
            {confirm && (
              <div className="rounded-lg border border-accent/40 bg-surface p-4 shadow-sm">
                <p className="text-sm text-text-primary">
                  {confirm.when === 'now' ? 'Send' : 'Schedule'} “{c.subject}” to <strong>{summary.count} {summary.count === 1 ? 'person' : 'people'}</strong> at {summary.companies} customer{summary.companies === 1 ? '' : 's'}?
                </p>
                <p className="mt-1 text-xs text-text-muted">Each gets their own email. {summary.suppressed ? `${summary.suppressed} unsubscribed/bounced address(es) are skipped. ` : ''}The list is re-checked at send time.</p>
                {confirm.when === 'later' && <input type="datetime-local" value={confirm.at} onChange={(e) => setConfirm({ ...confirm, at: e.target.value })} className={`${field} max-w-xs`} />}
                <div className="mt-3 flex gap-2">
                  <button onClick={() => send(confirm.when === 'later' ? confirm.at : null)} disabled={busy === 'send' || (confirm.when === 'later' && !confirm.at)} className="rounded-md bg-accent px-4 py-2 text-sm font-semibold text-white hover:bg-accent-hover disabled:opacity-50">
                    {busy === 'send' ? 'Working…' : confirm.when === 'now' ? `Send to ${summary.count} now` : 'Schedule'}
                  </button>
                  <button onClick={() => setConfirm(null)} className="rounded-md border border-line px-3 py-2 text-sm text-text-secondary">Cancel</button>
                </div>
              </div>
            )}
          </div>

          <div className="space-y-3 xl:sticky xl:top-4 xl:self-start">
            <div className="flex flex-wrap items-center gap-2 text-xs">
              <span className="text-text-muted">Preview as</span>
              <select
                value={previewAs}
                onChange={(e) => {
                  setPreviewAs(e.target.value);
                  refresh(e.target.value, false);
                }}
                className="max-w-[16rem] rounded-md border border-line bg-surface px-2 py-1 text-xs"
              >
                <option value="">{summary?.recipients?.[0] ? `${summary.recipients[0].company} (first recipient)` : 'Sample client'}</option>
                {(summary?.recipients || []).slice(1, 300).map((r) => (
                  <option key={r.email} value={r.email}>
                    {r.company} · {r.name || r.email}
                    {r.currency ? ` · ${r.currency}` : ''}
                  </option>
                ))}
              </select>
              <span className="ml-auto flex gap-1">
                <button onClick={() => setMobile(false)} className={chip(!mobile)}>Desktop</button>
                <button onClick={() => setMobile(true)} className={chip(mobile)}>Phone</button>
              </span>
            </div>
            <div className="flex justify-center rounded-lg border border-line bg-surface-raised/40 p-2">
              <iframe title="Email preview" srcDoc={preview?.html || ''} className="h-[75vh] rounded-md border border-line bg-white" style={{ width: mobile ? 375 : '100%' }} sandbox="" />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// --- report ----------------------------------------------------------------------------------------
function AbPanel({ ab, id, status, onChanged }) {
  const [busy, setBusy] = useState(false);
  const pct = (x) => `${Math.round(x * 100)}%`;
  const pick = async (v) => {
    if (!window.confirm(v ? `Send subject ${v} to the ${ab.held} people still waiting?` : `Pick the winner now and send it to the ${ab.held} people still waiting?`)) return;
    setBusy(true);
    try {
      await pickAbWinner(id, v);
      onChanged();
    } finally {
      setBusy(false);
    }
  };
  const lead = ab.B.openRate > ab.A.openRate ? 'B' : ab.A.openRate > ab.B.openRate ? 'A' : null;
  return (
    <div className="mb-4 rounded-lg border border-line bg-surface p-4 shadow-sm">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <h3 className="text-sm font-semibold text-text-primary">A/B subject test</h3>
        <span className="text-xs text-text-muted">
          {ab.winner ? `Winner: subject ${ab.winner}` : status === 'TESTING' ? `${ab.held} waiting · winner picked ${ab.decideAt ? new Date(ab.decideAt).toLocaleString() : 'soon'}` : ''}
        </span>
      </div>
      <div className="mt-3 grid gap-3 sm:grid-cols-2">
        {['A', 'B'].map((v) => (
          <div key={v} className={`rounded-md border p-3 ${ab.winner === v ? 'border-success bg-success-light/40' : 'border-line'}`}>
            <div className="flex items-center gap-2 text-xs font-semibold text-text-muted">
              Subject {v}
              {ab.winner === v && <span className="rounded bg-success px-1.5 text-[10px] text-white">Winner</span>}
              {!ab.winner && lead === v && <span className="rounded bg-info-light px-1.5 text-[10px] text-info">Leading</span>}
            </div>
            <div className="mt-1 text-sm font-medium text-text-primary">{v === 'A' ? ab.subjectA : ab.subjectB}</div>
            <div className="mt-2 flex gap-4 text-xs text-text-secondary">
              <span><strong className="text-base tabular-nums text-text-primary">{pct(ab[v].openRate)}</strong> opened</span>
              <span><strong className="text-base tabular-nums text-text-primary">{pct(ab[v].clickRate)}</strong> clicked</span>
              <span className="self-end text-text-muted">of {ab[v].sent}</span>
            </div>
            {status === 'TESTING' && (
              <button disabled={busy} onClick={() => pick(v)} className="mt-2 text-xs font-medium text-accent hover:underline disabled:opacity-50">
                Send {v} to the rest now
              </button>
            )}
          </div>
        ))}
      </div>
      {status === 'TESTING' && (
        <button disabled={busy} onClick={() => pick(null)} className="mt-3 rounded-md border border-line px-3 py-1.5 text-xs font-medium text-text-secondary hover:bg-surface-raised disabled:opacity-50">
          Pick the winner now (more opens, then clicks)
        </button>
      )}
    </div>
  );
}

const fmtCur = (v, cur) => {
  try {
    return new Intl.NumberFormat(undefined, { style: 'currency', currency: cur, maximumFractionDigits: 0 }).format(v);
  } catch {
    return `${Math.round(v)} ${cur}`;
  }
};
/** "wholesale.teamm8.com/catalog · Style 2" — readable link labels for the report. */
function prettyLink(u) {
  try {
    const x = new URL(u);
    const q = x.searchParams.get('q');
    return `${x.host}${x.pathname}${q ? ` · ${q}` : ''}`;
  } catch {
    return u;
  }
}

function Report({ id }) {
  const [showEmail, setShowEmail] = useState(null);
  const [r, setR] = useState(null);
  const navigate = useNavigate();
  useEffect(() => {
    const load = () => fetchCampaignReport(id).then(setR).catch(() => {});
    load();
    const t = setInterval(load, 15000); // statuses arrive by webhook
    return () => clearInterval(t);
  }, [id]);
  if (!r) return <div className="text-sm text-text-muted">Loading…</div>;
  const t = r.totals;
  const pct = (n) => (t.sent ? `${Math.round((n / t.sent) * 100)}%` : '—');
  const Tile = ({ label, value, sub }) => (
    <div className="rounded-lg border border-line bg-surface p-3 shadow-sm">
      <div className="text-[11px] font-medium uppercase tracking-wide text-text-muted">{label}</div>
      <div className="mt-1 text-xl font-semibold tabular-nums text-text-primary">{value}</div>
      {sub && <div className="text-xs text-text-muted">{sub}</div>}
    </div>
  );
  const st = { held: 'Waiting for A/B winner', sent: 'Sent', delivered: 'Delivered', bounced: 'Bounced', complained: 'Marked spam', failed: 'Failed', skipped: 'Skipped', queued: 'Queued', sending: 'Sending' };
  return (
    <>
      <div className="mb-4 flex flex-wrap items-center justify-between gap-2 text-sm text-text-secondary">
        <span>
          {r.campaign.status === 'SENT' ? `Sent ${new Date(r.campaign.sentAt).toLocaleString()}` : r.campaign.status === 'TESTING' ? 'A/B test running — the rest go when the winner is picked' : r.campaign.status}
          {r.campaign.discountName && (
            <>
              {' · '}
              <Link to="/discounts" className="text-accent hover:underline">{r.campaign.discountName} results</Link>
            </>
          )}
        </span>
        <span className="flex gap-2">
          <button onClick={() => fetchPreview(id).then((p) => setShowEmail(p.html))} className="rounded-md border border-line px-3 py-1.5 text-sm font-medium text-text-secondary hover:bg-surface-raised">View email</button>
          <button onClick={() => duplicateCampaign(id).then((d) => navigate(`/marketing/${d.id}`))} className="rounded-md border border-line px-3 py-1.5 text-sm font-medium text-text-secondary hover:bg-surface-raised">Duplicate</button>
        </span>
      </div>
      {showEmail && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onMouseDown={(e) => e.target === e.currentTarget && setShowEmail(null)}>
          <div className="flex h-[90vh] w-full max-w-3xl flex-col rounded-lg bg-surface shadow-lg">
            <div className="flex items-center justify-between border-b border-line px-4 py-2 text-sm font-medium">
              {r.campaign.subject}
              <button onClick={() => setShowEmail(null)} className="rounded px-2 py-1 text-text-muted hover:bg-surface-raised" aria-label="Close">✕</button>
            </div>
            <iframe title="Sent email" srcDoc={showEmail} sandbox="" className="flex-1 rounded-b-lg bg-white" />
          </div>
        </div>
      )}
      {r.ab && <AbPanel ab={r.ab} id={id} status={r.campaign.status} onChanged={() => fetchCampaignReport(id).then(setR)} />}
      <div className="grid gap-3 sm:grid-cols-3 lg:grid-cols-6">
        <Tile label="Sent" value={t.sent} sub={t.skipped ? `${t.skipped} skipped` : null} />
        <Tile label="Delivered" value={t.delivered} sub={pct(t.delivered)} />
        <Tile label="Opened" value={t.opened} sub={pct(t.opened)} />
        <Tile label="Clicked" value={t.clicked} sub={pct(t.clicked)} />
        <Tile label="Bounced" value={t.bounced} sub={t.failed ? `${t.failed} failed` : null} />
        <Tile label="Unsubscribed" value={t.unsubscribed} />
      </div>
      {t.sent > 0 && t.delivered === 0 && t.opened === 0 && (
        <p className="mt-2 text-xs text-text-muted">Delivery and open tracking appear once the Resend webhook is connected.</p>
      )}
      <div className="mt-4 grid gap-3 lg:grid-cols-2">
        {r.orders && (
          <div className="rounded-lg border border-line bg-surface p-4 shadow-sm">
            <div className="flex items-baseline justify-between">
              <h3 className="text-sm font-semibold text-text-primary">Orders that followed</h3>
              <span className="text-xs text-text-muted">within {r.orders.days} days of sending</span>
            </div>
            <div className="mt-2 flex flex-wrap items-baseline gap-x-4 gap-y-1">
              <span className="text-xl font-semibold tabular-nums text-text-primary">{r.orders.count}</span>
              <span className="text-sm text-text-secondary">order{r.orders.count === 1 ? '' : 's'}{r.orders.count ? ` from ${r.orders.companies} customer${r.orders.companies === 1 ? '' : 's'}` : ''}</span>
              {Object.entries(r.orders.byCurrency).map(([cur, v]) => (
                <span key={cur} className="text-sm font-medium tabular-nums text-text-primary">{fmtCur(v, cur)}</span>
              ))}
            </div>
            {r.orders.orders.length > 0 && (
              <div className="mt-2 max-h-48 divide-y divide-line overflow-y-auto text-sm">
                {r.orders.orders.map((o) => (
                  <Link key={o.id} to={`/orders/${o.id}`} className="flex justify-between gap-2 py-1.5 hover:text-accent">
                    <span className="truncate">{o.company} <span className="text-text-muted">· {o.ref || 'pending'} · {new Date(o.createdAt).toLocaleDateString()}</span></span>
                    <span className="tabular-nums">{fmtCur(o.subtotal, o.currency)}</span>
                  </Link>
                ))}
              </div>
            )}
            <p className="mt-2 text-[11px] text-text-muted">Orders from customers who received this email, placed in the week after it went out.</p>
          </div>
        )}
        <div className="rounded-lg border border-line bg-surface p-4 shadow-sm">
          <h3 className="text-sm font-semibold text-text-primary">Links clicked</h3>
          {r.links?.length ? (
            <div className="mt-2 max-h-56 divide-y divide-line overflow-y-auto text-sm">
              {r.links.map((l) => (
                <div key={l.link} className="flex justify-between gap-3 py-1.5">
                  <span className="min-w-0 truncate text-text-secondary" title={l.link}>{prettyLink(l.link)}</span>
                  <span className="shrink-0 tabular-nums text-text-primary">{l.people} <span className="text-xs text-text-muted">{l.people === 1 ? 'person' : 'people'}</span></span>
                </div>
              ))}
            </div>
          ) : (
            <p className="mt-2 text-sm text-text-muted">No clicks recorded yet{t.sent && !t.delivered ? ' — click tracking needs the Resend webhook connected' : ''}.</p>
          )}
        </div>
      </div>
      <div className="mt-4 overflow-x-auto rounded-lg border border-line bg-surface shadow-sm">
        <table className="w-full text-sm">
          <thead className="bg-surface-raised text-left text-xs text-text-muted">
            <tr>
              <th className="px-3 py-2 font-medium">Customer</th>
              <th className="px-3 py-2 font-medium">Recipient</th>
              {r.ab && <th className="px-3 py-2 font-medium">Subject</th>}
              <th className="px-3 py-2 font-medium">Status</th>
              <th className="px-3 py-2 font-medium">Opened</th>
              <th className="px-3 py-2 font-medium">Clicked</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-line">
            {r.recipients.map((x) => (
              <tr key={x.email}>
                <td className="px-3 py-1.5 text-text-primary">{x.company || '—'}</td>
                <td className="px-3 py-1.5 text-text-secondary">{x.name ? `${x.name} · ` : ''}{x.email}</td>
                {r.ab && <td className="px-3 py-1.5 text-text-muted">{x.variant ? `${x.variant} (test)` : r.ab.winner && x.status !== 'held' && x.status !== 'skipped' ? r.ab.winner : ''}</td>}
                <td className="px-3 py-1.5 text-text-secondary" title={x.error || ''}>{st[x.status] || x.status}{x.skipReason ? ` (${x.skipReason})` : ''}</td>
                <td className="px-3 py-1.5 text-text-muted">{x.openedAt ? new Date(x.openedAt).toLocaleDateString() : ''}</td>
                <td className="px-3 py-1.5 text-text-muted">{x.clickedAt ? new Date(x.clickedAt).toLocaleDateString() : ''}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
}

export default function MarketingCampaign() {
  const { id } = useParams();
  const [c, setC] = useState(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const load = () => fetchCampaign(id).then(setC).catch((e) => setError(errMsg(e)));
  useEffect(() => {
    setC(null);
    load();
  }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  if (error) return <p className="text-sm text-error">{error}</p>;
  if (!c) return <div className="text-sm text-text-muted">Loading…</div>;
  // Drafts, failed sends and emails scheduled for later stay editable; once it's going out (send now,
  // or its time has come) it's the report.
  const editable = ['DRAFT', 'FAILED'].includes(c.status) || (c.status === 'SCHEDULED' && new Date(c.scheduledAt).getTime() > Date.now() + 60e3);
  return (
    <>
      <div className="mb-4">
        <Link to="/marketing" className="text-sm text-text-secondary hover:text-text-primary">← Marketing</Link>
        <h1 className="mt-1 text-xl font-semibold tracking-tight text-text-primary">{c.name}</h1>
      </div>
      {editable && c.status === 'SCHEDULED' && (
        <div className="mb-4 flex flex-wrap items-center justify-between gap-2 rounded-md border border-accent/40 bg-accent-light px-3 py-2 text-sm">
          <span>Scheduled for {new Date(c.scheduledAt).toLocaleString()}. Changes you make are saved and go out with it.</span>
          <button disabled={busy} onClick={() => { setBusy(true); unscheduleCampaign(c.id).then(setC).finally(() => setBusy(false)); }} className="font-medium text-accent hover:underline">Cancel schedule</button>
        </div>
      )}
      {c.status === 'FAILED' && c.lastError && <div className="mb-4 rounded-md bg-error-light px-3 py-2 text-sm text-error">Last attempt failed: {c.lastError}. Fix it and send again.</div>}
      {editable ? (
        <Composer key={c.id} initial={c} onSent={(r) => setC({ ...c, ...r })} />
      ) : (
        <Report id={c.id} />
      )}
    </>
  );
}
